import { Button } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useCallback, useRef, useState } from 'react';
import { Cropper, type CropperRef } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { t } from 'i18next';
import { resizeImageFile } from '@pocapan/shared/utils/resizeImage';

interface ProfileImageCropProps {
  imageSrc: string;
  onCropComplete: (croppedImageUrl: string) => void;
  onBack: () => void;
}

const ProfileImageCrop = ({ imageSrc, onCropComplete, onBack }: ProfileImageCropProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const cropperRef = useRef<CropperRef>(null);

  const onDownloadClick = useCallback(async () => {
    if (!cropperRef.current) return;
    setIsLoading(true);

    try {
      const cropper = cropperRef.current;
      const sourceCanvas = cropper.getCanvas();
      if (!sourceCanvas) return;

      const blob: Blob = await new Promise((resolve) =>
        sourceCanvas.toBlob((b) => resolve(b as Blob), 'image/png')
      );

      if (!blob) {
        throw new Error('Failed to generate cropped image blob');
      }
      const file = new File([blob], 'profile-crop.png', { type: 'image/png' });
      const resizedBase64 = (await resizeImageFile(file, {
        maxWidth: 512,
        maxHeight: 512,
        format: 'PNG',
        outputType: 'base64',
        quality: 100,
        rotation: 0
      })) as string;

      onCropComplete(resizedBase64);
    } catch (error) {
      console.log('이미지 크롭 오류:', error);
    } finally {
      setIsLoading(false);
    }
  }, [onCropComplete]);

  const handleBackClick = () => {
    onBack();
  };

  return (
    <div className="bg-pure-black min-h-screen pt-[env(safe-area-inset-top)] fixed inset-0 flex flex-col">
      <header className="fixed top-[env(safe-area-inset-top)] left-0 right-0 z-50 px-[16px] py-[16px]">
        <ArrowLeft size={24} className="text-main-dark-1" onClick={handleBackClick} />
      </header>

      <div className="flex flex-col items-center justify-center my-auto">
        <div className="w-full max-w-[100%] h-[60vh]">
          <Cropper
            ref={cropperRef}
            src={imageSrc}
            stencilProps={{ aspectRatio: 1 }}
            className="w-full h-full"
          />
        </div>
      </div>

      <div className="fixed left-0 right-0 bg-pure-black px-[24px] bottom-0 pb-[calc(env(safe-area-inset-bottom)+10px)]">
        <p className="text-[15px] font-medium text-[#FFFFFF] leading-[18px] text-center mb-[24px]">
          {t('BettieSetting.profileImage.imageCropDescription')}
        </p>
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={onDownloadClick}
          disabled={isLoading}
        >
          {t('confirm')}
        </Button>
      </div>
    </div>
  );
};

export default ProfileImageCrop;
